import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Book, FileDown } from 'lucide-react';
import { accountingAPI } from '@/lib/api';
import { toast } from 'sonner';
import { LEDGER_TYPE_META, RECIPIENT_TYPE_LABEL, money, fmtDate, TOASTS } from '@/lib/accountingConstants';
import EmptyState from '@/components/accounting/EmptyState';
import TableSkeleton from '@/components/accounting/TableSkeleton';

const ENTRY_TYPES = Object.keys(LEDGER_TYPE_META);

export default function LedgerExplorerPage() {
  // Prefill filters from URL query (deep-links from profile pages)
  const urlParams = React.useMemo(() => new URLSearchParams(window.location.search), []);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    recipient_type: urlParams.get('recipient_type') || '',
    recipient_id: urlParams.get('recipient_id') || '',
    entry_types: [],
    date_from: '', date_to: '', amount_min: '', amount_max: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (filters.recipient_type) params.recipient_type = filters.recipient_type;
      if (filters.recipient_id)   params.recipient_id = filters.recipient_id;
      if (filters.entry_types?.length) params.entry_types = filters.entry_types.join(',');
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to)   params.date_to = filters.date_to;
      if (filters.amount_min !== '') params.amount_min = Number(filters.amount_min);
      if (filters.amount_max !== '') params.amount_max = Number(filters.amount_max);
      const { data } = await accountingAPI.ledgerQuery(params);
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load ledger'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const toggleType = (t) => {
    setFilters((f) => {
      const has = f.entry_types.includes(t);
      return { ...f, entry_types: has ? f.entry_types.filter((x) => x !== t) : [...f.entry_types, t] };
    });
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem('token') || '';
      const params = {};
      if (filters.recipient_type) params.recipient_type = filters.recipient_type;
      if (filters.recipient_id)   params.recipient_id = filters.recipient_id;
      if (filters.entry_types?.length) params.entry_types = filters.entry_types.join(',');
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to)   params.date_to = filters.date_to;
      const url = `${process.env.REACT_APP_BACKEND_URL}/api${accountingAPI.ledgerExportUrl(params)}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = objUrl; a.download = 'ledger.csv'; a.click();
      URL.revokeObjectURL(objUrl);
    } catch { toast.error(TOASTS.EXPORT_FAILED); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="ledger-explorer-page">
      <PageHeader
        title="Ledger Explorer"
        description="Immutable, append-only recipient ledger — the single source of truth for every financial movement."
        breadcrumbs={[{ label: 'Accounting', to: '/accounting' }, { label: 'Ledger Explorer' }]}
        actions={
          <Button variant="outline" onClick={exportCsv} data-testid="ledger-export-btn">
            <FileDown className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        }
      />

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Recipient Type</Label>
              <Select value={filters.recipient_type || 'any'} onValueChange={(v) => setFilters({ ...filters, recipient_type: v === 'any' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="agency">Agency</SelectItem>
                  <SelectItem value="subagency">Subagency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Recipient ID (exact)</Label>
              <Input value={filters.recipient_id} onChange={(e) => setFilters({ ...filters, recipient_id: e.target.value })} placeholder="UUID / NPN" data-testid="filter-recipient-id" />
            </div>
            <div>
              <Label>Date From</Label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
            </div>
            <div>
              <Label>Date To</Label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Amount Min</Label>
              <Input type="number" step="0.01" value={filters.amount_min} onChange={(e) => setFilters({ ...filters, amount_min: e.target.value })} />
            </div>
            <div>
              <Label>Amount Max</Label>
              <Input type="number" step="0.01" value={filters.amount_max} onChange={(e) => setFilters({ ...filters, amount_max: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label>Entry Types</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {ENTRY_TYPES.map((t) => (
                  <button key={t} onClick={() => toggleType(t)}
                    className={`text-[10px] px-2 py-1 rounded-full border font-medium ${
                      filters.entry_types.includes(t) ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                    }`}
                    data-testid={`entry-type-toggle-${t}`}>
                    {LEDGER_TYPE_META[t]?.label || t.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <TableSkeleton rows={6} cols={8} />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={Book}
              title="No Ledger Entries"
              description="No entries match the current filters. Clear filters to view the full ledger."
              dataTestId="ledger-empty-state"
            />
          ) : (
            <>
              <div className="px-4 py-2 border-b border-slate-200 text-xs text-slate-500 flex items-center justify-between">
                <div><b>{rows.length}</b> shown · {total} total matching</div>
              </div>
              <div className="overflow-x-auto max-h-[70vh]">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 sticky top-0 z-10">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Date</th>
                      <th className="text-left px-3 py-2 font-medium">Recipient</th>
                      <th className="text-left px-3 py-2 font-medium">Type</th>
                      <th className="text-right px-3 py-2 font-medium">Balance Before</th>
                      <th className="text-right px-3 py-2 font-medium">Amount</th>
                      <th className="text-right px-3 py-2 font-medium">Balance After</th>
                      <th className="text-left px-3 py-2 font-medium">Reference</th>
                      <th className="text-left px-3 py-2 font-medium">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((e) => {
                      const tMeta = LEDGER_TYPE_META[e.entry_type] || { label: e.entry_type, tone: 'bg-slate-100 text-slate-700 border-slate-300' };
                      return (
                      <tr key={e.entry_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ledger-row-${e.entry_id}`}>
                        <td className="px-3 py-2 text-slate-700 text-xs">{fmtDate(e.created_at)}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-800">{e.recipient_name || e.recipient_id}</div>
                          <div className="text-[10px] uppercase text-slate-500">{RECIPIENT_TYPE_LABEL[e.recipient_type] || e.recipient_type}</div>
                        </td>
                        <td className="px-3 py-2">
                          <Badge className={`${tMeta.tone} border font-normal text-[10px]`}>
                            {tMeta.label}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-right text-slate-500">{money(e.balance_before)}</td>
                        <td className={`px-3 py-2 text-right font-medium ${e.amount >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{money(e.amount)}</td>
                        <td className="px-3 py-2 text-right font-medium">{money(e.balance_after)}</td>
                        <td className="px-3 py-2 font-mono text-xs">{e.payment_reference || e.batch_number || e.statement_number || e.adjustment_number || '—'}</td>
                        <td className="px-3 py-2 text-xs text-slate-600">{e.notes || e.reason || '—'}</td>
                      </tr>);
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
